import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiChevronRight, FiClipboard } from "react-icons/fi";
import ChildHealthProfilePanel from "../ChildHealthProfilePanel";

/**
 * Health profiles of the children of the family, shown in place of the family overview (like a child profile):
 * pick a child on the left, view or edit its profile on the right. The doctor and emergency contact are shared by
 * the family, so they can be changed from any child.
 */
const FamilyHealthProfileView = ({ t, familyChildren = [], onBack }) => {
  const [selectedId, setSelectedId] = useState(familyChildren[0]?.id ?? null);

  useEffect(() => {
    if (!familyChildren.some((child) => String(child.id) === String(selectedId))) {
      setSelectedId(familyChildren[0]?.id ?? null);
    }
  }, [familyChildren, selectedId]);

  const selected = familyChildren.find((child) => String(child.id) === String(selectedId)) || null;

  return (
    <div className="space-y-4 text-slate-950 dark:text-slate-100">
      <div className="flex min-w-0 items-center gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/35 p-5 shadow-[0_16px_42px_-34px_rgba(15,23,42,0.35)] dark:border-emerald-900/60 dark:bg-emerald-950/20">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-emerald-100 bg-white text-emerald-800 shadow-sm transition hover:-translate-x-0.5 hover:border-emerald-200 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-900 dark:text-emerald-200 dark:hover:bg-emerald-950/30"
          aria-label={t("common.back", { defaultValue: "Back" })}
        >
          <FiArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-200">
          <FiClipboard className="h-6 w-6" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
            {t("parentPortal.health.eyebrow", { defaultValue: "For outings and emergencies" })}
          </p>
          <h2 className="mt-1 truncate text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
            {t("parentPortal.health.heading", { defaultValue: "Contacts & notes" })}
          </h2>
        </div>
      </div>

      {familyChildren.length === 0 ? (
        <p className="rounded-2xl border border-slate-100 bg-white p-5 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950/20 dark:text-slate-400">
          {t("parentPortal.health.no_children", { defaultValue: "There are no children at school in your family." })}
        </p>
      ) : (
        <div className="grid items-start gap-5 xl:grid-cols-[minmax(16rem,0.42fr)_minmax(0,1.58fr)]">
          <aside className="self-start rounded-2xl border border-slate-100 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/20">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              {t("parentPortal.children.children", { defaultValue: "Children" })}
            </h3>
            <div className="mt-4 space-y-2" role="tablist" aria-orientation="vertical">
              {familyChildren.map((child) => {
                const active = String(child.id) === String(selectedId);
                const name = [child.firstName, child.lastName].filter(Boolean).join(" ") ||
                  t("common.unknown_child", { defaultValue: "Child" });
                return (
                  <button
                    key={child.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setSelectedId(child.id)}
                    className={`flex w-full min-w-0 items-center gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                      active
                        ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20"
                        : "border-slate-100 bg-white hover:border-emerald-200 hover:bg-emerald-50/60 dark:border-slate-800 dark:bg-slate-950/20 dark:hover:bg-emerald-950/20"
                    }`}
                  >
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-sm font-bold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">
                      {(child.firstName || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-950 dark:text-white">{name}</span>
                    <FiChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="min-h-[24rem] rounded-2xl border border-slate-100 bg-white p-5 dark:border-slate-800 dark:bg-slate-950/20">
            {selected && (
              <ChildHealthProfilePanel key={selected.id} t={t} childId={selected.id} childName={selected.firstName} />
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default FamilyHealthProfileView;
